import React, { useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';

import { Filter } from '../../../Enum/Filters';
import InterestUri from '../../../Enum/InterestUri';
import { Cell, HeaderCell, Row as RowInterface } from '../../../Interface/Hook/Interest/InterestTableDTO';
import { Sort } from '../../../Interface/Table/Sort';

import { useGetExportPdfRequest } from '../../../Hook/Interest/UseGetExportPdfRequest';
import { makePdf } from '../../../Service/Interest/PdfService';
import { enablePdfButton, pdfFinished } from '../../../Store/Pdf/action';
import { useGetFilterValuesSelected } from '../../../Hook/Interest/UseGetPdfFilterData';
import { isLabelArea, shouldCheck } from '../../../Helpers/Interest/PdfHelper';
import { isPropertyAttributes } from '../../../Service/Interest/Pdf/CellToStringConverter';

interface Props {
    filterSettings?: Filter[];
    headers: HeaderCell[];
    rows: RowInterface[];
    scopable?: boolean;
    section: InterestUri;
    sort?: Sort;
}

export const ExportPdf: React.FC<Props> = ({ filterSettings, headers, rows, section, scopable, sort }: Props) => {
    const dispatch = useDispatch();
    const shouldPrint = useGetExportPdfRequest();
    const link = useRef<HTMLAnchorElement>(null);
    const filterValuesSelected = useGetFilterValuesSelected();

    useEffect(() => {
        dispatch(enablePdfButton());
    }, []);

    const getFilteredHeaders = useCallback(() => {
        if (!shouldCheck(section) || filterValuesSelected.length === 0) {
            return headers;
        }

        return headers.reduce((acc: HeaderCell[], header: HeaderCell) => {
            if (header.alwaysUseInPdf) {
                return [...acc, header];
            }

            if (!filterValuesSelected.includes(header.prop as string)) {
                return acc;
            }

            return [...acc, header];
        }, []);
    }, [headers, filterValuesSelected]);

    const cellHasSelectedFilterValue = (cell: Cell): boolean => {
        if (!isPropertyAttributes(cell.value) || !shouldCheck(section)) {
            return false;
        }

        if (isLabelArea(section)) {
            return filterValuesSelected.includes(cell.value.finDataCode as string);
        }

        return filterValuesSelected.includes(cell.value?.property?.category as string);
    }

    const getPreparedRows = useCallback(() => {
        return rows.reduce((acc: RowInterface[], row: RowInterface) => {
            const newRow = { ...row };
            newRow.cells = row.cells.reduce((acc: Cell[], cell: Cell) => {
                if (cell.alwaysUseInPdf || !shouldCheck(section)) {
                    return [...acc, cell];
                }

                if (!cellHasSelectedFilterValue(cell)) {
                    return acc;
                }

                return [...acc, cell];
            }, []);

            return [ ...acc, newRow];
        }, []);
    }, [rows, filterValuesSelected]);

    useEffect(() => {
        if (!shouldPrint || !link.current) {
            return;
        }

        link.current.href = window.URL.createObjectURL(
            makePdf(getPreparedRows(), getFilteredHeaders(), section, scopable, sort, filterSettings),
        );
        link.current.click();

        dispatch(pdfFinished());
    }, [shouldPrint, filterValuesSelected]);

    return <a target="_blank" ref={link} />;
};
